import Routes from "@navigations/Routes";
import {
  isEmbeddedFrame,
  parentResizesIframe,
  postIframeLayout,
} from "@utils/iframeMessaging";
import { isShownScreen } from "./contentBounds";
import { SEARCH_EMPTY_ROOT_ID } from "./searchEmptyHeight";

const MIN_CHANGE = 4;
const LAYOUT_SETTLE_MS = 120;

export type SearchResultsHeightMode = "web" | "ipad" | "mobile";

type SearchResultsHeightOptions = {
  mode: SearchResultsHeightMode;
  view: string | number;
};

type RestoredStyle = {
  height: string;
  maxHeight: string;
  overflow: string;
  overflowY: string;
  flexGrow: string;
  flexShrink: string;
  flexBasis: string;
};

const getVerticalListScrollers = (root: HTMLElement) => {
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>("div")
  ).filter((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      element.scrollHeight - element.clientHeight >= MIN_CHANGE
    );
  });

  // The FlashList viewport can sit inside a larger screen scroller. Only the
  // deepest overflowing viewport owns the results scroll.
  return candidates.filter(
    (candidate) =>
      !candidates.some(
        (other) => other !== candidate && candidate.contains(other)
      )
  );
};

const saveStyle = (element: HTMLElement): RestoredStyle => ({
  height: element.style.height,
  maxHeight: element.style.maxHeight,
  overflow: element.style.overflow,
  overflowY: element.style.overflowY,
  flexGrow: element.style.flexGrow,
  flexShrink: element.style.flexShrink,
  flexBasis: element.style.flexBasis,
});

const restoreStyle = (element: HTMLElement, style: RestoredStyle) => {
  element.style.height = style.height;
  element.style.maxHeight = style.maxHeight;
  element.style.overflow = style.overflow;
  element.style.overflowY = style.overflowY;
  element.style.flexGrow = style.flexGrow;
  element.style.flexShrink = style.flexShrink;
  element.style.flexBasis = style.flexBasis;
};

const isGridView = (view: SearchResultsHeightOptions["view"]) =>
  String(view) === "grid";

// FlashList sizes the grid canvas to every row, then only mounts the rows
// inside the viewport. Measuring those mounted cards misses the rows below
// and leaves Load More on top of the last visible card.
const getGridCanvas = (root: HTMLElement) => {
  let canvas: HTMLElement | null = null;
  let canvasHeight = 0;
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    if (element.querySelector("#iframe-load-more")) return;
    const inlineHeight = element.style?.height;
    if (!inlineHeight?.endsWith("px")) return;
    const height = parseFloat(inlineHeight);
    if (!(height > canvasHeight)) return;
    const holdsGridCards = Array.from(element.children).some(
      (child) =>
        child instanceof HTMLElement &&
        window.getComputedStyle(child).position === "absolute" &&
        child.getBoundingClientRect().width >= 100
    );
    if (!holdsGridCards) return;
    canvas = element;
    canvasHeight = height;
  });
  return canvas;
};

// Read the grid height FlashList already computed. Do not write it back onto
// the canvas or its parents: those writes become the next measurement and the
// iframe grows on every pass.
const measureGridHeight = (root: HTMLElement) => {
  const canvas = getGridCanvas(root);
  if (!canvas) return 0;
  const canvasHeight = parseFloat(canvas.style.height);
  if (!(canvasHeight > 40)) return 0;

  const canvasTop = canvas.getBoundingClientRect().top;
  let cardOverflow = 0;
  canvas.querySelectorAll<HTMLElement>("div").forEach((card) => {
    if (window.getComputedStyle(card).position !== "absolute") return;
    const rect = card.getBoundingClientRect();
    if (rect.width < 100) return;
    cardOverflow = Math.max(
      cardOverflow,
      rect.bottom - (canvasTop + canvasHeight)
    );
  });
  // A card can paint slightly past the canvas. Cap it so a stretched card
  // cannot become the next iframe height and start another growth pass.
  cardOverflow = Math.min(Math.max(0, cardOverflow), 240);

  const loadMore = root.querySelector<HTMLElement>("#iframe-load-more");
  const loadMoreBottom = loadMore?.getBoundingClientRect().bottom ?? 0;
  const contentEnd = Math.max(
    canvasTop + canvasHeight + cardOverflow + 8,
    loadMoreBottom
  );
  return Math.ceil(Math.max(0, contentEnd + window.scrollY));
};

// Grid content can sit a few pixels past the list viewport. That is enough
// for the list to keep its own scroll. Grow it once, and only by that sliver,
// so the adjustment cannot compound into a taller iframe.
const GRID_SCROLL_SLACK = 16;
const gridScrollSlack = new WeakMap<HTMLElement, number>();

const settleGridScroll = (root: HTMLElement) => {
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    const style = window.getComputedStyle(element);
    if (style.overflowY !== "auto" && style.overflowY !== "scroll") return;
    const extra = element.scrollHeight - element.clientHeight;
    if (extra <= 0 || extra > GRID_SCROLL_SLACK) return;
    const used = gridScrollSlack.get(element) ?? 0;
    if (used >= GRID_SCROLL_SLACK) return;
    gridScrollSlack.set(element, used + extra);
    element.style.height = `${element.scrollHeight}px`;
    element.style.flexGrow = "0";
  });
};

const expandLoadedPage = (
  root: HTMLElement,
  restoredStyles: Map<HTMLElement, RestoredStyle>
) => {
  restoredStyles.forEach((_, scroller) => {
    if (!scroller.isConnected || !root.contains(scroller)) {
      restoredStyles.delete(scroller);
      return;
    }
    const contentHeight = scroller.scrollHeight;
    if (contentHeight > scroller.clientHeight) {
      scroller.style.height = `${contentHeight}px`;
    }
  });

  getVerticalListScrollers(root).forEach((scroller) => {
    if (!restoredStyles.has(scroller)) {
      restoredStyles.set(scroller, saveStyle(scroller));
    }
    const contentHeight = scroller.scrollHeight;
    scroller.style.height = `${contentHeight}px`;
    scroller.style.maxHeight = "none";
    scroller.style.overflowY = "hidden";
    scroller.style.flexGrow = "0";
    scroller.style.flexShrink = "0";
  });
};

// iOS Safari can leave aspect-ratio boxes (card images and their empty
// placeholders) at zero height after a frozen screen is shown again. Only
// rebuilding the box recovers it.
const rebuildCollapsedAspectBoxes = (root: HTMLElement) => {
  const collapsed = Array.from(
    root.querySelectorAll<HTMLElement>("div")
  ).filter((element) => {
    if (window.getComputedStyle(element).aspectRatio === "auto") return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 1 && rect.height < 2;
  });
  if (!collapsed.length) return;
  const displays = collapsed.map((element) => element.style.display);
  collapsed.forEach((element) => {
    element.style.display = "none";
  });
  // Reading layout flushes the display change before it is restored.
  const reflow = root.offsetHeight;
  if (reflow < 0) return;
  collapsed.forEach((element, index) => {
    element.style.display = displays[index];
  });
};

const getSizedContentBottom = (root: HTMLElement) => {
  const loadMore = root.querySelector<HTMLElement>("#iframe-load-more");
  const sized: { element: HTMLElement; bottom: number }[] = [];
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    if (element.id === "iframe-load-more") return;
    const inlineHeight = element.style?.height;
    if (!inlineHeight?.endsWith("px")) return;
    const height = parseFloat(inlineHeight);
    if (!(height > 40)) return;
    const rect = element.getBoundingClientRect();
    if (rect.height <= 0) return;
    sized.push({ element, bottom: rect.top + height });
  });

  let contentEnd = 0;
  sized.forEach(({ element, bottom }) => {
    if (loadMore && element.contains(loadMore)) return;
    if (
      sized.some(
        (other) => other.element !== element && element.contains(other.element)
      )
    ) {
      return;
    }
    if (bottom > contentEnd) contentEnd = bottom;
  });

  return contentEnd;
};

const getInFlowContentBottom = (root: HTMLElement) => {
  let bottom = 0;
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    const style = window.getComputedStyle(element);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.position === "fixed" ||
      style.position === "absolute"
    ) {
      return;
    }
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0)
      bottom = Math.max(bottom, rect.bottom);
  });
  return bottom;
};

// Each mode uses the finite, currently loaded page. It never uses the whole
// document scrollHeight, so loading another page is the only pagination event
// that can grow the iframe.
const measureSearchResultsHeight = (
  root: HTMLElement,
  mode: SearchResultsHeightMode
) => {
  const loadMore = root.querySelector<HTMLElement>("#iframe-load-more");
  const loadMoreRect = loadMore?.getBoundingClientRect();
  const loadMoreBottom = loadMoreRect
    ? loadMoreRect.top + (loadMoreRect.height || loadMore?.offsetHeight || 0)
    : 0;
  const sizedBottom = getSizedContentBottom(root);
  const flowBottom = getInFlowContentBottom(root);
  // Desktop tables, iPad's horizontal table and mobile cards render
  // differently, but all expose a finite FlashList content canvas. The
  // ordinary flow bottom is only a fallback because flex wrappers can retain
  // the preceding screen's iframe height.
  let contentEnd =
    mode === "ipad"
      ? sizedBottom || loadMoreBottom || flowBottom
      : sizedBottom || flowBottom;

  if (loadMoreBottom > 0) {
    contentEnd = Math.max(contentEnd, loadMoreBottom);
  }

  return contentEnd > 0 ? Math.ceil(contentEnd) : 0;
};

const scrollableContentBottom = (root: HTMLElement) => {
  let bottom = 0;
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    if (element.scrollHeight <= element.clientHeight + MIN_CHANGE) return;
    bottom = Math.max(
      bottom,
      element.getBoundingClientRect().top +
        window.scrollY +
        element.scrollHeight
    );
  });
  return bottom > 0 ? Math.ceil(bottom) : 0;
};

export const initializeSearchResultsHeight = (
  getRoot: () => HTMLElement | null,
  { mode, view }: SearchResultsHeightOptions
) => {
  if (!isEmbeddedFrame()) return () => undefined;

  let active = true;
  let lastPosted = 0;
  let frameId = 0;
  let settleTimer = 0;
  const restoredStyles = new Map<HTMLElement, RestoredStyle>();

  const publish = () => {
    if (!active) return;
    const root = getRoot();
    if (!root || typeof root.querySelectorAll !== "function") return;
    // Blur freezes this screen instead of unmounting it. Keep its watcher from
    // posting the short results height over the filter screen.
    if (!isShownScreen(root)) return;
    // The empty state sizes the iframe itself. Forget the last height so the
    // first result page after it is always posted.
    const empty = root.querySelector<HTMLElement>(`#${SEARCH_EMPTY_ROOT_ID}`);
    if (empty && isShownScreen(empty)) {
      lastPosted = 0;
      return;
    }
    rebuildCollapsedAspectBoxes(root);
    // Leave the list's own scroll in place until the parent page proves it
    // resizes this frame. Old embeds ignore the height message.
    const releaseScroll = parentResizesIframe();
    if (releaseScroll && isGridView(view)) settleGridScroll(root);
    if (releaseScroll && !isGridView(view)) {
      expandLoadedPage(root, restoredStyles);
    }
    const height = isGridView(view)
      ? measureGridHeight(root)
      : measureSearchResultsHeight(root, mode);
    const posted = Math.max(height, scrollableContentBottom(root));
    if (posted <= 0 || Math.abs(posted - lastPosted) < MIN_CHANGE) return;
    lastPosted = posted;
    postIframeLayout({
      mode: "auto-height",
      route: Routes.SearchResultScreen,
      height: posted,
    });
  };

  const schedule = () => {
    cancelAnimationFrame(frameId);
    window.clearTimeout(settleTimer);
    frameId = requestAnimationFrame(() => {
      settleTimer = window.setTimeout(publish, LAYOUT_SETTLE_MS);
    });
  };

  const root = getRoot();
  const mutationObserver = new MutationObserver(schedule);
  const resizeObserver = new ResizeObserver(schedule);
  if (root) {
    mutationObserver.observe(root, { childList: true, subtree: true });
    resizeObserver.observe(root);
  }
  window.addEventListener("resize", schedule);
  schedule();

  return () => {
    active = false;
    cancelAnimationFrame(frameId);
    window.clearTimeout(settleTimer);
    mutationObserver.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("resize", schedule);
    restoredStyles.forEach((style, element) => restoreStyle(element, style));
    restoredStyles.clear();
  };
};

export const cleanupSearchResultsHeight = (cleanup: () => void) => cleanup();
